import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Datatable } from 'ui';
import { CreateNewPortfolioModal } from 'modules/common/Modals';

import { orderTypes } from 'shared/constants/orderTypes';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { removeItemsFromPortfolio, putNotifyError } from 'shared/functions';
import { useTable, usePortfolioModals } from 'shared/hooks';
import { exportService } from 'services/export';
import { ratingTypes } from 'shared/constants/ratingTypes';

import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { useInstrumentsRatingRequest } from 'modules/creditRating/hooks/useInstrumentsRatingRequest';
import { useInstrumentRenderers } from 'modules/creditRating/hooks/useInstrumentRenderers';
import { ItemType } from 'shared/types/portfolios';
import InstrumentsFiltersModal from './InstrumentsFiltersModal';

const InstrumentsTable = ({
  dataSelector,
  actionCreator,
  actionCreatorParams,
  searchQuery,
  dossierId,
  ratingType,
  onItemsRemoved,
  initialFilter,
  initialFilterModalValues,
  renderDatatable,
}) => {
  const { t } = useTranslation();
  const didUnmount = useRef(false);
  const dispatch = useDispatch();
  const [selected, setSelected] = useState({ items: {}, count: 0 });
  const [isPending, setIsPending] = useState(false);
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: null });

  const onRequested = () => {
    setShouldRefresh({ value: true, reason: refreshReasons.PRODUCT_REQUESTED });
    dispatch(refreshTable);
  };

  const { requestLoading, requestRating } = useInstrumentsRatingRequest({ onRequested });
  const { nameRenderer, ratingRenderer, addedDateRenderer, protectedRenderer } =
    useInstrumentRenderers();
  const isPortfolioTable = !!dossierId;

  const [instrumentsFiltersModalOpen, setInstrumentsFiltersModalOpen] = useState(false);
  const [dossiersRequest, setDossiersRequest] = useState({ items: {}, count: 0, type: null });

  const toggleInstrumentsFiltersModal = () =>
    setInstrumentsFiltersModalOpen(!instrumentsFiltersModalOpen);

  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const onSelectedChange = (selectedItems) => {
    setSelected({ ...selectedItems });
    setDossiersRequest({ ...selectedItems, type: ItemType.SCOPEONEID });
  };

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  const buildInitialColumns = useCallback(
    () => [
      { name: 'ratingType', label: 'Rating type', options: { sort: false } },
      { name: 'extendedData', label: 'Extended Data', options: { display: 'excluded' } },
      {
        name: 'name',
        label: 'Name',
        options: {
          sort: !isPortfolioTable,
          customBodyRender: nameRenderer,
        },
      },
      {
        name: 'isin',
        label: 'ISIN',
        options: {
          sort: !isPortfolioTable,
          customBodyRender: protectedRenderer,
        },
      },
      {
        name: 'entityName',
        label: 'Entity',
        options: { sort: !isPortfolioTable, customBodyRender: protectedRenderer },
      },
      {
        name: 'instrumentRating',
        label: 'Instrument rating',
        options: {
          sort: false,
          customBodyRender: ratingRenderer,
        },
      },
      {
        name: 'maturityDate',
        label: 'Maturity date',
        options: { sort: !isPortfolioTable, customBodyRender: protectedRenderer },
      },
      {
        name: 'couponPercent',
        label: 'Coupon',
        options: { sort: !isPortfolioTable, customBodyRender: protectedRenderer },
      },
      {
        name: 'issueVolume',
        label: 'Instrument volume',
        options: { sort: !isPortfolioTable, customBodyRender: protectedRenderer },
      },
      {
        name: 'addedDate',
        label: 'Added',
        options: {
          display: isPortfolioTable,
          sort: false,
          setCellHeaderProps: () => ({ align: 'right' }),
          customBodyRender: addedDateRenderer,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [initialColumns, setInitialColumns] = useState(buildInitialColumns());

  useEffect(() => {
    setInitialColumns(buildInitialColumns());
  }, [buildInitialColumns]);

  const columnsFilterKeysMapping = [
    null,
    null,
    null,
    null,
    'ratingType',
    ['ratingFrom', 'ratingTo'],
    ['maturityDateFrom', 'maturityDateTo'],
    ['couponRangeFrom', 'couponRangeTo'],
    null,
    ['issueVolumeFrom', 'issueVolumeTo', 'issueVolumeCurrency'],
  ];

  const { columns, pagination, onTableChange, onSortChange, refreshTable, setFilter, isFiltered } =
    useTable({
      initialColumns,
      columnsFilterKeysMapping,
      getTableDataAction: actionCreator,
      getTableDataActionParams: actionCreatorParams,
      initialFilter,
    });

  const dossierItemsIds = (items) => Object.values(items).map((itemData) => itemData[2]);
  const exportItemsIds = (items) => Object.values(items).map((itemData) => itemData[0][0]);

  const removeItems = (items) => {
    const ids = dossierItemsIds(items);
    setIsPending(true);
    removeItemsFromPortfolio({ dossierId, dossierItemsIds: ids, ratingType })
      .then(() => {
        if (!didUnmount.current) {
          setShouldRefresh({ value: true, reason: refreshReasons.DELETED });
          dispatch(refreshTable);
        }
      })
      .finally(() => {
        if (!didUnmount.current) {
          setIsPending(false);
          onItemsRemoved();
        }
      });
  };

  const handleInstrumentsFiltersModalSubmit = (values) => {
    const {
      countries,
      industrySectors,
      ratingFrom,
      ratingTo,
      couponRangeFrom,
      couponRangeTo,
      issueVolumeFrom,
      issueVolumeTo,
      maturityDateFrom,
      maturityDateTo,
      issueVolumeCurrency,
    } = values;

    const newFilters = {
      ...(countries.length && { countries }),
      ...(industrySectors.length !== 0 && { industrySectors }),
      ...(values.ratingType !== null && { ratingType: [values.ratingType] }),
      ...(ratingFrom !== 'D' && { ratingFrom }),
      ...(ratingTo !== 'AAA' && { ratingTo }),
      ...(couponRangeFrom !== 0 && { couponRangeFrom }),
      ...(couponRangeTo !== 15 && { couponRangeTo }),
      ...(issueVolumeFrom !== 100000000 && { issueVolumeFrom }),
      ...(issueVolumeTo !== 1000000000 && { issueVolumeTo }),
      ...(maturityDateFrom !== null && { maturityDateFrom }),
      ...(maturityDateTo !== null && { maturityDateTo }),
      ...(issueVolumeCurrency !== null && { issueVolumeCurrency }),
    };

    setFilter(newFilters);
    setShouldRefresh({ value: true, reason: refreshReasons.FILTERS_APPLIED });
    setInstrumentsFiltersModalOpen(false);
  };

  const downloadErrorHandler = () => {
    putNotifyError(t('Failed to export instruments'));
  };

  const exportToExcel = () => {
    exportService({
      items: exportItemsIds(selected.items),
      type: orderTypes.INSTRUMENT,
      onError: downloadErrorHandler,
    });
  };

  const toolbarData = [
    {
      text: 'Request selected',
      handler: requestRating,
      disabled: !selected.count,
      hidden: true,
      data: selected.items,
    },
    {
      text: 'Add to portfolio',
      handler: addToPortfolio.show,
      hidden: !selected.count || isPortfolioTable,
    },
    {
      text: 'Export to Excel',
      handler: exportToExcel,
      hidden: !selected.count,
    },
    { text: 'Share', hidden: true },
    {
      text: 'Delete',
      handler: removeItems,
      data: selected.itemIds,
      hidden: !isPortfolioTable || !selected.count,
    },
  ];

  return (
    <>
      <Datatable
        loading={dataSelector.isPending || isPending || requestLoading || !renderDatatable}
        columns={columns}
        pagination={pagination}
        data={renderDatatable ? dataSelector.data : []}
        total={dataSelector.total}
        onSelectedChange={onSelectedChange}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        toolbarData={toolbarData}
        isFiltered={isFiltered}
        onApplyFiltersClick={() => setInstrumentsFiltersModalOpen(true)}
        shouldRefresh={shouldRefresh}
        showFilters={!isPortfolioTable}
        removeShadow
      />
      <InstrumentsFiltersModal
        open={instrumentsFiltersModalOpen}
        onClose={toggleInstrumentsFiltersModal}
        onSubmit={handleInstrumentsFiltersModalSubmit}
        searchQuery={searchQuery}
        initialFilter={initialFilterModalValues}
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={dossiersRequest}
        itemType="INSTRUMENT"
      />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        itemsData={dossiersRequest}
        itemType="INSTRUMENT"
      />
    </>
  );
};

InstrumentsTable.defaultProps = {
  actionCreatorParams: {},
  searchQuery: null,
  dossierId: null,
  onItemsRemoved: () => null,
  initialFilter: {},
  initialFilterModalValues: {},
  ratingType: ratingTypes.CREDIT_RATING,
  renderDatatable: true,
};

InstrumentsTable.propTypes = {
  dataSelector: PropTypes.shape({
    isPending: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    data: PropTypes.arrayOf(PropTypes.any).isRequired,
  }).isRequired,
  actionCreator: PropTypes.func.isRequired,
  actionCreatorParams: PropTypes.object,
  searchQuery: PropTypes.string,
  dossierId: PropTypes.string,
  onItemsRemoved: PropTypes.func,
  initialFilter: PropTypes.object,
  initialFilterModalValues: PropTypes.object,
  ratingType: PropTypes.oneOf(Object.values(ratingTypes)),
  renderDatatable: PropTypes.bool,
};

export default InstrumentsTable;
